import { useCallback } from 'react';
import xhr from 'xhr';

/**
 * Wraps las llamadas al backend del mecanismo de vinculación manual.
 * Toda la lógica de estado vive en linkPaymentsReducer; este hook solo
 * expone fetchCandidates y saveBatch como funciones estables.
 */
export function useLinkPaymentsData() {
  const fetchCandidates = useCallback(async ({ enrollmentType, enrollmentId, studentId, programId }) => {
    const params = new URLSearchParams();
    params.set('enrollment_type', enrollmentType);
    if (enrollmentId) params.set('enrollment_id', String(enrollmentId));
    if (studentId) params.set('student_id', String(studentId));
    if (programId) params.set('program_id', String(programId));
    const res = await xhr.get(`/payments/manual-link/candidates?${params.toString()}`);
    // Strapi custom returns {data: {enrollments, payments, meta}}.
    const payload = res?.data?.data || res?.data || {};
    const meta = payload.meta || {};
    return {
      enrollments: Array.isArray(payload.enrollments) ? payload.enrollments : [],
      payments: Array.isArray(payload.payments) ? payload.payments : [],
      claimedOrdinalsByEnrollment: payload.claimedOrdinalsByEnrollment || {},
      meta: {
        enrollmentType: meta.enrollmentType || enrollmentType,
        studentId: meta.studentId || studentId || null,
        programId: meta.programId || programId || null,
        subjectCount: meta.subjectCount || 0,
      },
    };
  }, []);

  const unlinkPayment = useCallback(async (paymentId, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.enrollmentType) params.set('enrollment_type', opts.enrollmentType);
    if (opts.enrollmentId) params.set('enrollment_id', String(opts.enrollmentId));
    const qs = params.toString();
    const url = qs ? `/payments/manual-link/${paymentId}?${qs}` : `/payments/manual-link/${paymentId}`;
    const res = await xhr.delete(url);
    return res?.data?.data || {};
  }, []);

  const setAnchorOverride = useCallback(async ({ enrollmentType, enrollmentId, date, note }) => {
    const body = { enrollment_type: enrollmentType, enrollment_id: enrollmentId, date };
    if (date != null) body.note = note;
    const res = await xhr.put('/payments/anchor-override', { data: body });
    return res?.data?.data || {};
  }, []);

  const saveBatch = useCallback(async (items) => {
    const res = await xhr.put('/payments/manual-link/batch', { data: { items } });
    const payload = res?.data?.data || res?.data || {};
    return {
      saved: Array.isArray(payload.saved) ? payload.saved : [],
      failed: Array.isArray(payload.failed) ? payload.failed : [],
    };
  }, []);

  return { fetchCandidates, saveBatch, unlinkPayment, setAnchorOverride };
}
